'use client';

import { type RefObject, useCallback, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';

import { unmask } from '@packages/gql/src/betterMasking';
import type { SearchProductCardItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardItemFragmentFragmentDoc';
import type { SearchProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardResultFragmentFragmentDoc';

import { ProductCardGridItem } from '@packages/modules/src/ProductCardV2/ProductCardV2.GridItem.client';

import { useProductImpressionTracking } from './tracking/useProductImpressionTracking';
import { showSeenProductsAtom } from '../../atoms/numberOfSeenProducts';
import { useDeferredRendering } from './useDeferredRendering';
import {
  SearchProductCardContent,
  type SearchProductCardContentProps,
} from './SearchProductCard.Content.client';
import { SearchProductCardPlaceholderContent } from './SearchProductCard.PlaceholderContent';
import { useOnFirstBecameVisible } from '../../hooks/useOnFirstBecameVisible';

export type SearchProductCardProps = Omit<SearchProductCardContentProps, 'hasUserInteraction'> & {
  alwaysRender?: boolean;
  onFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
  'aria-setsize'?: number;
  'aria-posinset'?: number;
  indexInPage: number;
  /**
   * When true, defers expensive hydration until the card comes into view.
   * Used for below-fold cards to reduce initial TBT.
   */
  deferred?: boolean;
};

type SearchProductCardVisibleProps = SearchProductCardContentProps & {
  elementRef: RefObject<HTMLLIElement | null>;
  onFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
  indexInPage: number;
};

/**
 * Hydrated part of the card, split out so its costly per-card work (impression tracking, jotai
 * subscription, impression observer) only mounts when full contents render. Off-screen cards stay
 * un-hydrated until scrolled near, keeping that cost off the main thread (progressive hydration).
 */
const SearchProductCardVisible = ({
  elementRef,
  onFirstBecameVisible,
  indexInPage,
  ...contentProps
}: SearchProductCardVisibleProps) => {
  const { maskedItem, maskedResult, pageNumber } = contentProps;

  const item = unmask<typeof SearchProductCardItemFragmentFragmentDoc>(maskedItem);
  const result = unmask<typeof SearchProductCardResultFragmentFragmentDoc>(maskedResult);

  const { dispatchProductImpressionTracking } = useProductImpressionTracking(result, item);
  const showSeenProducts = useAtomValue(showSeenProductsAtom);

  useOnFirstBecameVisible(elementRef, () => {
    dispatchProductImpressionTracking();
    onFirstBecameVisible?.({ pageNumber, indexInPage });

    if (showSeenProducts) {
      elementRef.current?.classList.add('seen');
    }
  });

  return <SearchProductCardContent {...contentProps} />;
};

/**
 * Specialized version of `ProductCard` for the search product grids
 */
export const SearchProductCard = ({
  alwaysRender,
  onFirstBecameVisible,
  indexInPage,
  deferred = false,
  ...contentProps
}: SearchProductCardProps) => {
  const {
    maskedItem,
    maskedResult,
    'aria-setsize': ariaSetsize,
    'aria-posinset': ariaPosinset,
    pageNumber,
  } = contentProps;

  const [hasUserInteraction, setHasUserInteraction] = useState(false);

  const item = unmask<typeof SearchProductCardItemFragmentFragmentDoc>(maskedItem);

  const ref = useRef<HTMLLIElement>(null);

  const handleUserInteraction = useCallback(() => {
    setHasUserInteraction(true);
  }, []);

  // TODO SEARCH-3514 check again if we can just skip deferred rendering altogether, once the cards are even more optimized (wishlist button, color button tooltips) to get rendering time down from the current ~50ms to maybe 25ms or less
  const { showFullContents } = useDeferredRendering({
    ref,
    alwaysRender: alwaysRender || !deferred,
  });

  const { styleId, isHighlight } = item;

  return (
    <ProductCardGridItem
      className={`group pb-2 ${isHighlight ? 'highlight' : ''}`}
      ref={ref}
      id={styleId}
      onFocus={handleUserInteraction}
      onMouseOver={handleUserInteraction}
      aria-setsize={ariaSetsize}
      aria-posinset={ariaPosinset}
    >
      {showFullContents ? (
        <SearchProductCardVisible
          elementRef={ref}
          onFirstBecameVisible={onFirstBecameVisible}
          indexInPage={indexInPage}
          {...contentProps}
          hasUserInteraction={hasUserInteraction}
        />
      ) : (
        <SearchProductCardPlaceholderContent
          maskedResult={maskedResult}
          maskedItem={maskedItem}
          pageNumber={pageNumber}
        />
      )}
    </ProductCardGridItem>
  );
};
